/**
 * Copyright © 2025 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect, useCallback } from "react";
import { Select, MenuItem, FormControl, Box, CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";
import apiHeaders from "../apiHeaders";
import { handleSessionExpiredClose } from "../logout";
import { setTenantId } from "../storageUtils";

function CompanyDropdown() {
    const [companies, setCompanies] = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState(sessionStorage.getItem("tenantId") || "");
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    // Function to fetch companies from API
    const fetchCompaniesData = useCallback(async (tenantId) => {
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_CONNECTOR_API_URL}/api/company/connections?tenantId=${tenantId}`,
                {
                    method: "GET",
                    headers: {
                        ...apiHeaders,
                        "Authorization": `${sessionStorage.getItem('token')}`
                    },
                }
            );

            if (response.status === 401) {
                handleSessionExpiredClose();
                return;
            }

            if (!response.ok) {
                throw new Error("Failed to fetch companies.");
            }

            const data = await response.json();
            // API returns array of objects with tenantId and tenantName
            setCompanies(data);
            // Cache companies in sessionStorage
            sessionStorage.setItem("companies", JSON.stringify(data));
        } catch (err) {
            console.error("Failed to load companies", err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch companies from API on initial load
    useEffect(() => {
        const fetchCompanies = async () => {
            const currentTenantId = sessionStorage.getItem("tenantId");
            console.log("CompanyDropdown: Initial load, tenantId:", currentTenantId);
            
            if (!currentTenantId) {
                console.log("CompanyDropdown: No tenantId, not fetching");
                return; // Don't fetch if no tenantId
            }

            // Check if companies are already cached in sessionStorage
            const cachedCompanies = sessionStorage.getItem("companies");
            if (cachedCompanies) {
                try {
                    const parsed = JSON.parse(cachedCompanies);
                    console.log("CompanyDropdown: Using cached companies:", parsed);
                    setCompanies(parsed);
                    return;
                } catch (e) {
                    console.error("Failed to parse cached companies", e);
                }
            }

            console.log("CompanyDropdown: Fetching companies from API");
            fetchCompaniesData(currentTenantId);
        };

        fetchCompanies();
    }, [fetchCompaniesData]);

    // Listen for tenantId changes and refetch if needed
    useEffect(() => {
        const handleTenantChange = () => {
            const newTenantId = sessionStorage.getItem("tenantId");
            console.log("CompanyDropdown: TenantId changed to:", newTenantId);
            setSelectedTenantId(newTenantId || "");
            
            // If tenantId was just set and we don't have companies yet, fetch them
            if (newTenantId && companies.length === 0) {
                const cachedCompanies = sessionStorage.getItem("companies");
                if (cachedCompanies) {
                    try {
                        const parsed = JSON.parse(cachedCompanies);
                        console.log("CompanyDropdown: Using cached companies after tenant change:", parsed);
                        setCompanies(parsed);
                    } catch (e) {
                        console.error("Failed to parse cached companies", e);
                        // If parsing fails, trigger a refetch
                        fetchCompaniesData(newTenantId);
                    }
                } else {
                    // No cached data, fetch from API
                    console.log("CompanyDropdown: No cache, fetching from API");
                    fetchCompaniesData(newTenantId);
                }
            }
        };
        window.addEventListener("TenantIdChange", handleTenantChange);
        return () => window.removeEventListener("TenantIdChange", handleTenantChange);
    }, [companies.length, fetchCompaniesData]);

    const handleCompanyChange = (event) => {
        const newTenantId = event.target.value;
        setSelectedTenantId(newTenantId);
        
        // Update sessionStorage
        setTenantId(newTenantId);
        window.dispatchEvent(new Event("TenantIdChange"));

        // Update URL with new tenantId and refresh the page
        const pathParts = location.pathname.split('/');
        if (pathParts.length >= 2) {
            const basePath = pathParts[1]; // e.g., 'settings', 'services', etc.
            // Construct new path with updated tenantId
            const newPath = `/${basePath}/${newTenantId}${location.search}`;
            // Use window.location.href to force a full page refresh with new tenantId
            window.location.href = newPath;
        }
    };

    // Don't show dropdown if not logged in
    if (!selectedTenantId) {
        return null;
    }

    return (
        <Box sx={{ minWidth: 200, maxWidth: 300, marginLeft: 2, zIndex: 1300, position: 'relative' }}>
            <FormControl 
                fullWidth 
                size="small"
                sx={{
                    zIndex: 1300,
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: '#3A5CB8',
                        borderRadius: '20px',
                        color: 'white',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        '& fieldset': {
                            borderColor: 'transparent',
                            borderRadius: '20px',
                        },
                        '&:hover': {
                            backgroundColor: '#4668C5',
                        },
                        '&:hover fieldset': {
                            borderColor: 'transparent',
                        },
                        '&.Mui-focused': {
                            backgroundColor: '#4668C5',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: 'transparent',
                        },
                    },
                    '& .MuiSvgIcon-root': {
                        color: 'white',
                        pointerEvents: 'none',
                    },
                }}
            >
                <Select
                    value={selectedTenantId}
                    onChange={handleCompanyChange}
                    onClick={(e) => e.stopPropagation()}
                    displayEmpty
                    disabled={loading || companies.length === 0}
                    sx={{
                        color: 'white',
                        backgroundColor: '#3A5CB8',
                        borderRadius: '20px',
                        paddingLeft: '4px',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                        '.MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& .MuiSelect-select': {
                            paddingTop: '8px',
                            paddingBottom: '8px',
                            paddingLeft: '16px',
                            paddingRight: '32px',
                        },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                marginTop: '8px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                zIndex: 1400,
                                '& .MuiMenuItem-root': {
                                    color: '#333',
                                    padding: '10px 16px',
                                    '&:hover': {
                                        backgroundColor: '#F0F4F8',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: '#E6F0FA',
                                        '&:hover': {
                                            backgroundColor: '#D6E8F7',
                                        },
                                    },
                                },
                            },
                        },
                    }}
                >
                    {loading ? (
                        <MenuItem value={selectedTenantId}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <span>Loading companies...</span>
                            </Box>
                        </MenuItem>
                    ) : companies.length > 0 ? (
                        companies.map((company) => (
                            <MenuItem key={company.tenantId} value={company.tenantId}>
                                {company.tenantName}
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem value={selectedTenantId}>
                            No companies available
                        </MenuItem>
                    )}
                </Select>
            </FormControl>
        </Box>
    );
}

export default CompanyDropdown;

